import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { toast } from 'react-hot-toast';
import { LayoutDashboard } from 'lucide-react';
import { triggerBrowserDownload, trackDirectDownload } from '../../../utils/downloadResume';
import { apiUrl } from '../../../utils/apiBase';
import { defaultLogoCdnUrl, legacyApiPath } from '../../../config/environments';

export default function Navbar({ onOpenContact, onOpenDownload, onOpenLogin }) {
  const { isAuthenticated, logout, user } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [resourcesDropdownOpen, setResourcesDropdownOpen] = useState(false);
  const [tryNowDropdownOpen, setTryNowDropdownOpen] = useState(false);
  const [manifestVersion, setManifestVersion] = useState('1.0.4');
  const [availableVersions, setAvailableVersions] = useState([
    { version: '1.0.4', label: 'Latest Version (v1.0.4)', initial_download_link: '/candor-exe/CandorAI-Setup-v1.0.4.zip' }
  ]);
  const tryNowRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    fetch('/update_manifest.json')
      .then(res => {
        if (res.ok) return res.json();
        throw new Error('Failed to load');
      })
      .then(data => {
        if (data) {
          if (data.version) {
            setManifestVersion(data.version);
          }
          if (data.versions && Array.isArray(data.versions)) {
            setAvailableVersions(data.versions);
          } else {
            setAvailableVersions([
              {
                version: data.version || '1.0.4',
                label: 'Current Version',
                initial_download_link: data.initial_download_link || `/candor-exe/CandorAI-Setup-v${data.version || '1.0.4'}.zip`
              }
            ]);
          }
        }
      })
      .catch(err => {
        console.warn('Could not fetch update_manifest.json:', err);
      });
  }, []);

  useEffect(() => {
    function handleClickOutside(event) {
      if (tryNowRef.current && !tryNowRef.current.contains(event.target)) {
        setTryNowDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (err) {
      console.error('Logout failed:', err);
    }
  };

  const handleSelectVersion = async (typeOrVersion) => {
    if (typeof typeOrVersion === 'object' && typeOrVersion !== null) {
      const downloadUrl = typeOrVersion.initial_download_link || typeOrVersion.downloadUrl || `/candor-exe/CandorAI-Setup-v${typeOrVersion.version}.zip`;
      if (isAuthenticated) {
        triggerBrowserDownload(downloadUrl);
        toast.success(`Your Candor IDE v${typeOrVersion.version} download has started!`);
        trackDirectDownload(user, downloadUrl, 'header_nav_dropdown');
      } else {
        sessionStorage.setItem('candor_pending_download', JSON.stringify({ url: downloadUrl }));
        onOpenDownload();
      }
    } else if (typeOrVersion === 'previous') {
      const previousVersionUrl = `/candor-exe/CandorAI-Setup-v${manifestVersion}.zip`;
      if (isAuthenticated) {
        triggerBrowserDownload(previousVersionUrl);
        toast.success(`Your Candor IDE v${manifestVersion} download has started!`);
        trackDirectDownload(user, previousVersionUrl, 'header_nav_dropdown');
      } else {
        sessionStorage.setItem('candor_pending_download', JSON.stringify({ url: previousVersionUrl }));
        onOpenDownload();
      }
    } else if (typeOrVersion === 'latest') {
      if (isAuthenticated) {
        const payload = {
          first_name: user?.first_name || user?.name?.split(' ')[0] || 'User',
          last_name: user?.last_name || user?.name?.split(' ').slice(1).join(' ') || '',
          title: user?.title || 'User',
          company_name: user?.company || 'N/A',
          email: user?.email || '',
          phone: user?.phone || '',
          subject: 'Request Access to Preview Version',
          message: `User ${user?.first_name || ''} ${user?.last_name || ''} (${user?.email || ''}) from ${user?.company || 'N/A'} is requesting access to the preview version of Candor SI.`
        };
        try {
          const res = await fetch(apiUrl(process.env.REACT_APP_ENDPOINT_AUTH_CONTACT_US || legacyApiPath('contact_us')), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          });
          if (!res.ok) {
            throw new Error('Failed to submit contact request');
          }
          toast.success('Your request for the preview version has been sent successfully!');
        } catch (err) {
          toast.error(err.message || 'Failed to submit request. Please try again.');
        }
      } else {
        const subject = 'Request Access to Preview Version';
        const message = 'I would like to request access to the preview version of Candor SI. Please send me the download details.';
        onOpenContact(subject, message);
      }
    }
  };

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/' || location.pathname === '';
    }
    return location.pathname === path;
  };

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-xl border-b border-gray-100 shadow-[0_4px_20px_-10px_rgba(0,0,0,0.05)] transition-all duration-300">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <div className="flex-shrink-0 flex items-center">
              <Link to="/" className="flex items-center">
                <img
                  src={`${process.env.PUBLIC_URL || ''}/candor-brand-lockup.png`}
                  alt="Candor — Vibe Coding for Data Platform"
                  className="h-12 w-auto object-contain"
                  style={{ maxWidth: '180px' }}
                  onError={(e) => {
                    e.target.src = `${process.env.PUBLIC_URL || ''}/candorlogo.png`;
                    e.target.onerror = () => { e.target.src = defaultLogoCdnUrl(); };
                  }}
                />
              </Link>
            </div>

            {/* Desktop Navigation Links */}
            <nav className="hidden lg:flex items-center space-x-2">
              <Link
                to="/"
                className={`px-4 py-2 rounded-full text-[15px] font-semibold transition-all duration-200 ${
                  isActive('/') ? 'text-[#1D4ED8] bg-blue-50' : 'text-gray-600 hover:text-[#1D4ED8] hover:bg-gray-50'
                }`}
              >
                Home
              </Link>
              <Link
                to="/about"
                className={`px-4 py-2 rounded-full text-[15px] font-semibold transition-all duration-200 ${
                  isActive('/about') ? 'text-[#1D4ED8] bg-blue-50' : 'text-gray-600 hover:text-[#1D4ED8] hover:bg-gray-50'
                }`}
              >
                About
              </Link>
              <Link
                to="/features"
                className={`px-4 py-2 rounded-full text-[15px] font-semibold transition-all duration-200 ${
                  isActive('/features') ? 'text-[#1D4ED8] bg-blue-50' : 'text-gray-600 hover:text-[#1D4ED8] hover:bg-gray-50'
                }`}
              >
                Features
              </Link>
              <Link
                to="/pricing"
                className={`px-4 py-2 rounded-full text-[15px] font-semibold transition-all duration-200 ${
                  isActive('/pricing') ? 'text-[#1D4ED8] bg-blue-50' : 'text-gray-600 hover:text-[#1D4ED8] hover:bg-gray-50'
                }`}
              >
                Pricing
              </Link>
              <Link
                to="/download"
                className={`px-4 py-2 rounded-full text-[15px] font-semibold transition-all duration-200 ${
                  isActive('/download') ? 'text-[#1D4ED8] bg-blue-50' : 'text-gray-600 hover:text-[#1D4ED8] hover:bg-gray-50'
                }`}
              >
                Download
              </Link>

              {/* Resources Dropdown */}
              <div
                className="relative"
                onMouseEnter={() => setResourcesDropdownOpen(true)}
                onMouseLeave={() => setResourcesDropdownOpen(false)}
              >
                <button
                  aria-expanded={resourcesDropdownOpen}
                  aria-haspopup="true"
                  aria-label="Toggle Resources Menu"
                  onClick={() => setResourcesDropdownOpen(!resourcesDropdownOpen)}
                  className={`px-4 py-2 rounded-full text-[15px] font-semibold transition-all duration-200 inline-flex items-center focus-visible:ring-2 focus-visible:ring-[#1D4ED8] ${
                    resourcesDropdownOpen || isActive('/blog') || isActive('/newsletter') || isActive('/press-release') || isActive('/security')
                      ? 'text-[#1D4ED8] bg-blue-50'
                      : 'text-gray-600 hover:text-[#1D4ED8] hover:bg-gray-50'
                  }`}
                >
                  Resources
                  <svg
                    className={`ml-1 h-4 w-4 transition-transform duration-200 ${
                      resourcesDropdownOpen ? 'transform rotate-180' : ''
                    }`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {resourcesDropdownOpen && (
                  <div role="menu" className="absolute left-1/2 transform -translate-x-1/2 w-48 rounded-xl bg-white shadow-xl border border-slate-100 py-2 mt-0 transition-all duration-200 z-50">
                    <Link
                      to="/blog"
                      role="menuitem"
                      className="block px-4 py-2 text-sm text-slate-700 hover:bg-blue-50/50 hover:text-blue-600"
                    >
                      Blog
                    </Link>
                    <Link
                      to="/newsletter"
                      role="menuitem"
                      className="block px-4 py-2 text-sm text-slate-700 hover:bg-blue-50/50 hover:text-blue-600"
                    >
                      Newsletter
                    </Link>
                    <Link
                      to="/press-release"
                      role="menuitem"
                      className="block px-4 py-2 text-sm text-slate-700 hover:bg-blue-50/50 hover:text-blue-600"
                    >
                      Press Release
                    </Link>
                    <Link
                      to="/security"
                      role="menuitem"
                      className="block px-4 py-2 text-sm text-slate-700 hover:bg-blue-50/50 hover:text-blue-600"
                    >
                      Security
                    </Link>
                  </div>
                )}
              </div>
            </nav>

            {/* CTAs */}
            <div className="hidden lg:flex items-center space-x-3">
              <button
                onClick={onOpenContact}
                aria-label="Open Contact Us modal"
                className="px-5 py-2.5 rounded-full text-[15px] font-bold text-gray-700 hover:text-[#1D4ED8] hover:bg-gray-50 transition-colors border border-gray-200 focus-visible:ring-2 focus-visible:ring-[#1D4ED8]"
              >
                Contact Us
              </button>

              {/* Try Now Dropdown */}
              <div className="relative" ref={tryNowRef}>
                <button
                  onClick={() => setTryNowDropdownOpen(!tryNowDropdownOpen)}
                  aria-expanded={tryNowDropdownOpen}
                  aria-haspopup="true"
                  aria-label="Toggle Try Now options dropdown"
                  className="px-6 py-2.5 rounded-full text-[15px] font-bold text-white bg-[#1D4ED8] hover:bg-blue-800 shadow-sm transition-all hover:-translate-y-0.5 inline-flex items-center gap-1 focus-visible:ring-2 focus-visible:ring-[#1D4ED8]"
                >
                  Try Now
                  <svg
                    className={`h-4 w-4 transition-transform duration-200 ${
                      tryNowDropdownOpen ? 'transform rotate-180' : ''
                    }`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {tryNowDropdownOpen && (
                  <div className="absolute right-0 w-64 rounded-xl bg-white shadow-xl border border-slate-100 py-2 mt-2 transition-all duration-200 z-50 text-left">
                    {availableVersions.map((v, idx) => (
                      <button
                        key={v.version}
                        onClick={() => {
                          setTryNowDropdownOpen(false);
                          handleSelectVersion(v);
                        }}
                        className={`w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-blue-50/50 hover:text-blue-600 transition-colors ${
                          idx > 0 ? 'border-t border-slate-100' : ''
                        }`}
                      >
                        <div className="font-semibold">{v.label || 'Current Version'}</div>
                        <div className="text-xs text-slate-400 mt-0.5">Setup v{v.version} (ZIP)</div>
                      </button>
                    ))}
                    <button
                      onClick={() => {
                        setTryNowDropdownOpen(false);
                        handleSelectVersion('latest');
                      }}
                      className="w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-blue-50/50 hover:text-blue-600 transition-colors border-t border-slate-100"
                    >
                      <div className="font-semibold">Preview Version</div>
                      <div className="text-xs text-slate-400 mt-0.5">Request access validation</div>
                    </button>
                    <Link
                      to="/download"
                      onClick={() => setTryNowDropdownOpen(false)}
                      className="block w-full text-left px-4 py-2 text-xs font-bold text-blue-600 hover:bg-blue-50/50 hover:text-blue-700 transition-colors border-t border-slate-100"
                    >
                      All Platforms &amp; Builds (Win, Mac, Linux) →
                    </Link>
                    <Link
                      to="/download#previous-releases"
                      onClick={() => setTryNowDropdownOpen(false)}
                      className="block w-full text-left px-4 py-2 text-xs font-semibold text-slate-500 hover:bg-slate-50 hover:text-slate-800 transition-colors border-t border-slate-100"
                    >
                      Older Versions &amp; Archive (v1.0.3, v1.0.2...) →
                    </Link>
                  </div>
                )}
              </div>

              {/* Download & Auth Actions */}
              {isAuthenticated ? (
                <>
                  <Link
                    to="/download"
                    className="px-6 py-2.5 rounded-full text-[15px] font-bold text-[#1D4ED8] bg-white border-2 border-[#1D4ED8] hover:bg-blue-50 transition-all hover:-translate-y-0.5 inline-flex items-center"
                  >
                    Download
                  </Link>
                  <Link
                    to="/dashboard"
                    title="User Dashboard"
                    aria-label="User Dashboard"
                    className="px-4 py-2.5 rounded-full text-[15px] font-bold text-[#1D4ED8] bg-blue-50 hover:bg-blue-100 transition-all hover:-translate-y-0.5 flex items-center justify-center"
                  >
                    <LayoutDashboard className="h-5 w-5" />
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="px-4 py-2.5 rounded-full text-[15px] font-semibold text-gray-500 hover:text-gray-900 transition-colors"
                  >
                    Log Out
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/download"
                    className="px-6 py-2.5 rounded-full text-[15px] font-bold text-[#1D4ED8] bg-white border-2 border-[#1D4ED8] hover:bg-blue-50 transition-all hover:-translate-y-0.5 inline-flex items-center"
                  >
                    Download
                  </Link>
                  <button
                    onClick={onOpenLogin}
                    className="px-6 py-2.5 rounded-full text-[15px] font-bold text-gray-700 hover:text-[#1D4ED8] transition-colors"
                  >
                    Sign In
                  </button>
                </>
              )}
            </div>

            {/* Mobile hamburger menu */}
            <div className="flex lg:hidden">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="inline-flex items-center justify-center p-2 rounded-lg text-blue-600 hover:bg-blue-50 focus:outline-none"
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? (
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu panel */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-slate-100 bg-slate-50">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
              <Link
                to="/"
                onClick={() => setMobileMenuOpen(false)}
                className={`block px-3 py-2.5 rounded-xl text-base font-semibold transition-colors ${
                  isActive('/') ? 'text-blue-600 bg-blue-50' : 'text-slate-800 hover:bg-slate-100'
                }`}
              >
                Home
              </Link>
              <Link
                to="/about"
                onClick={() => setMobileMenuOpen(false)}
                className={`block px-3 py-2.5 rounded-xl text-base font-semibold transition-colors ${
                  isActive('/about') ? 'text-blue-600 bg-blue-50' : 'text-slate-800 hover:bg-slate-100'
                }`}
              >
                About
              </Link>
              <Link
                to="/features"
                onClick={() => setMobileMenuOpen(false)}
                className={`block px-3 py-2.5 rounded-xl text-base font-semibold transition-colors ${
                  isActive('/features') ? 'text-blue-600 bg-blue-50' : 'text-slate-800 hover:bg-slate-100'
                }`}
              >
                Features
              </Link>
              <Link
                to="/pricing"
                onClick={() => setMobileMenuOpen(false)}
                className={`block px-3 py-2.5 rounded-xl text-base font-semibold transition-colors ${
                  isActive('/pricing') ? 'text-blue-600 bg-blue-50' : 'text-slate-800 hover:bg-slate-100'
                }`}
              >
                Pricing
              </Link>
              <Link
                to="/download"
                onClick={() => setMobileMenuOpen(false)}
                className={`block px-3 py-2.5 rounded-xl text-base font-semibold transition-colors ${
                  isActive('/download') ? 'text-blue-600 bg-blue-50' : 'text-slate-800 hover:bg-slate-100'
                }`}
              >
                Download
              </Link>

              <div className="pt-2 pb-1 border-t border-slate-200">
                <div className="px-3 text-xs font-bold text-slate-400 uppercase tracking-wider">Resources</div>
                <Link
                  to="/blog"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block px-6 py-2 rounded-xl text-base font-semibold text-slate-700 hover:bg-slate-100"
                >
                  Blog
                </Link>
                <Link
                  to="/newsletter"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block px-6 py-2 rounded-xl text-base font-semibold text-slate-700 hover:bg-slate-100"
                >
                  Newsletter
                </Link>
                <Link
                  to="/press-release"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block px-6 py-2 rounded-xl text-base font-semibold text-slate-700 hover:bg-slate-100"
                >
                  Press Release
                </Link>
                <Link
                  to="/security"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block px-6 py-2 rounded-xl text-base font-semibold text-slate-700 hover:bg-slate-100"
                >
                  Security
                </Link>
              </div>

              <div className="pt-3 pb-2 border-t border-slate-200 space-y-2">
                <button
                  onClick={() => {
                    setMobileMenuOpen(false);
                    onOpenContact();
                  }}
                  className="w-full text-left px-3 py-2.5 rounded-xl text-base font-semibold text-slate-800 hover:bg-slate-100"
                >
                  Contact Us
                </button>

                {isAuthenticated ? (
                  <>
                    <Link
                      to="/dashboard"
                      onClick={() => setMobileMenuOpen(false)}
                      className="block text-center px-4 py-2.5 rounded-xl text-base font-bold text-white bg-blue-600 hover:bg-blue-700 shadow-md"
                    >
                      Dashboard
                    </Link>
                    <div className="space-y-1.5 border border-slate-200/80 rounded-xl p-2.5 bg-white">
                      <div className="text-xs font-bold text-slate-400 px-2 uppercase tracking-wider mb-1">Try Now Options</div>
                      {availableVersions.map(v => (
                        <button
                          key={v.version}
                          onClick={() => {
                            setMobileMenuOpen(false);
                            handleSelectVersion(v);
                          }}
                          className="w-full text-left px-3 py-2 rounded-lg text-sm font-semibold text-slate-700 hover:bg-blue-50/50 hover:text-blue-600 transition-colors border-b border-slate-100 last:border-b-0"
                        >
                          {v.label || 'Current Version'} (v{v.version})
                        </button>
                      ))}
                      <button
                        onClick={() => {
                          setMobileMenuOpen(false);
                          handleSelectVersion('latest');
                        }}
                        className="w-full text-left px-3 py-2 rounded-lg text-sm font-semibold text-slate-700 hover:bg-blue-50/50 hover:text-blue-600 transition-colors border-t border-slate-100"
                      >
                        Preview Version (Request Access)
                      </button>
                      <Link
                        to="/download"
                        onClick={() => setMobileMenuOpen(false)}
                        className="block w-full text-left px-3 py-2 text-xs font-bold text-blue-600 hover:bg-blue-50/50 hover:text-blue-700 transition-colors border-t border-slate-100"
                      >
                        All Platforms &amp; Builds →
                      </Link>
                      <Link
                        to="/download#previous-releases"
                        onClick={() => setMobileMenuOpen(false)}
                        className="block w-full text-left px-3 py-2 text-xs font-semibold text-slate-500 hover:bg-slate-50 hover:text-slate-800 transition-colors border-t border-slate-100"
                      >
                        Older Versions &amp; Archive →
                      </Link>
                    </div>
                    <Link
                      to="/download"
                      onClick={() => setMobileMenuOpen(false)}
                      className="w-full text-center px-4 py-2.5 rounded-xl text-base font-bold text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-lg block"
                    >
                      ⬇ Download App
                    </Link>
                    <button
                      onClick={() => {
                        setMobileMenuOpen(false);
                        handleLogout();
                      }}
                      className="w-full text-center px-4 py-2 rounded-xl text-base font-medium text-slate-500 hover:text-slate-800"
                    >
                      Log Out
                    </button>
                  </>
                ) : (
                  <div className="flex flex-col gap-2 pt-1">
                    <button
                      onClick={() => {
                        setMobileMenuOpen(false);
                        onOpenLogin();
                      }}
                      className="block w-full text-center px-4 py-2.5 rounded-xl text-base font-bold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200"
                    >
                      Login
                    </button>
                    <div className="space-y-1.5 border border-slate-200/80 rounded-xl p-2.5 bg-white">
                      <div className="text-xs font-bold text-slate-400 px-2 uppercase tracking-wider mb-1">Try Now Options</div>
                      {availableVersions.map(v => (
                        <button
                          key={v.version}
                          onClick={() => {
                            setMobileMenuOpen(false);
                            handleSelectVersion(v);
                          }}
                          className="w-full text-left px-3 py-2 rounded-lg text-sm font-semibold text-slate-700 hover:bg-blue-50/50 hover:text-blue-600 transition-colors border-b border-slate-100 last:border-b-0"
                        >
                          {v.label || 'Current Version'} (v{v.version})
                        </button>
                      ))}
                      <button
                        onClick={() => {
                          setMobileMenuOpen(false);
                          handleSelectVersion('latest');
                        }}
                        className="w-full text-left px-3 py-2 rounded-lg text-sm font-semibold text-slate-700 hover:bg-blue-50/50 hover:text-blue-600 transition-colors border-t border-slate-100"
                      >
                        Preview Version (Request Access)
                      </button>
                      <Link
                        to="/download"
                        onClick={() => setMobileMenuOpen(false)}
                        className="block w-full text-left px-3 py-2 text-xs font-bold text-blue-600 hover:bg-blue-50/50 hover:text-blue-700 transition-colors border-t border-slate-100"
                      >
                        All Platforms &amp; Builds →
                      </Link>
                      <Link
                        to="/download#previous-releases"
                        onClick={() => setMobileMenuOpen(false)}
                        className="block w-full text-left px-3 py-2 text-xs font-semibold text-slate-500 hover:bg-slate-50 hover:text-slate-800 transition-colors border-t border-slate-100"
                      >
                        Older Versions &amp; Archive →
                      </Link>
                    </div>
                    <Link
                      to="/download"
                      onClick={() => setMobileMenuOpen(false)}
                      className="w-full text-center px-4 py-2.5 rounded-xl text-base font-bold text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-lg block"
                    >
                      ⬇ Download App
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </header>
    </>
  );
}
